import { AnimatePresence } from 'framer-motion';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { TierListSection } from '@focuslee/shared-mini-app';

import { ListCategory, ListGoal, ListQuest } from '../../client/types';
import { DailyProgressExtended } from '../../hooks/useDailyProgress';
import { QuestItem } from './QuestItem';
import { RitualQuestSection } from './RitualQuestSection';

interface QuestDaySectionProps {
    sectionKey: string;
    quests: ListQuest[];
    isToday: boolean;
    dailyProgressData?: DailyProgressExtended | null;
    categoryMap: Record<string, ListCategory>;
    goalMap: Record<string, ListGoal>;
    onItemClick: (q: ListQuest) => void;
}

const sectionTranslationKeys = {
    MorningRitual: 'quest.sections.MorningRitual',
    EveningRitual: 'quest.sections.EveningRitual',
    Overdue: 'quest.sections.Overdue',
    'All day': 'quest.sections.All day',
    Morning: 'quest.sections.Morning',
    Afternoon: 'quest.sections.Afternoon',
    Evening: 'quest.sections.Evening',
} as const;

export const QuestDaySection: FC<QuestDaySectionProps> = ({
    sectionKey,
    quests,
    isToday,
    dailyProgressData,
    categoryMap,
    goalMap,
    onItemClick,
}) => {
    const { t } = useTranslation('common');
    if (sectionKey === 'MorningRitual' || sectionKey === 'EveningRitual') {
        const ritual = quests[0];
        if (!ritual) return null;

        return (
            <RitualQuestSection
                quest={ritual}
                sectionKey={sectionKey}
                dailyProgressData={dailyProgressData}
                onItemClick={onItemClick}
            />
        );
    }

    const sectionProgress = dailyProgressData?.progressMap[sectionKey];
    const isCompleted =
        sectionProgress != null && sectionProgress.done === sectionProgress.total;
    const showProgress = !!(isToday && sectionProgress);

    return (
        <TierListSection
            title={t(sectionTranslationKeys[sectionKey as keyof typeof sectionTranslationKeys] ?? 'quest.sections.All day')}
            headerColor={
                sectionKey === 'Overdue' ? 'var(--tg-theme-destructive-text-color)' : undefined
            }
            isCompleted={isCompleted}
            showProgress={showProgress}
            done={sectionProgress?.done ?? 0}
            total={sectionProgress?.total ?? quests.length}
            dotOverdue={sectionKey === 'Overdue'}
            summaryWhenNoProgress={t('quest.count', { count: quests.length })}
            sectionStyle={{ marginBottom: isCompleted ? 0 : undefined }}
            independentRows
        >
            <AnimatePresence>
                {quests.map((q) => (
                    <QuestItem
                        key={q.id}
                        quest={q}
                        categoryMap={categoryMap}
                        goalMap={goalMap}
                        onClick={() => onItemClick(q)}
                        overdue={sectionKey === 'Overdue'}
                    />
                ))}
            </AnimatePresence>
        </TierListSection>
    );
};
